import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, ExternalLink, Link2, Trash2, FileText, MoreVertical } from 'lucide-react'
import type { JobSummary } from '../../types'
import * as db from '../../services/db'
import EmptyState from '../ui/EmptyState'

function timeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const seconds = Math.floor((now - then) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return `${Math.floor(days / 30)}mo ago`
}

interface JobsPanelProps {
  onReopenJob: (id: string) => void
  onDeleteJob: (id: string) => void
}

export default function JobsPanel({ onReopenJob, onDeleteJob }: JobsPanelProps) {
  const navigate = useNavigate()
  const [summaries, setSummaries] = useState<JobSummary[]>([])
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const fetchSummaries = (q?: string) => {
    setLoading(true)
    db.fetchJobSummaries(q)
      .then(setSummaries)
      .catch(() => undefined)
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchSummaries()
  }, [])

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpenId) return
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpenId(null)
        setConfirmDeleteId(null)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpenId])

  const handleSearch = (value: string) => {
    setQuery(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchSummaries(value), 300)
  }

  const handleCopyLink = (summary: JobSummary) => {
    const url = `${window.location.origin}/share/${summary.shareToken}`
    navigator.clipboard.writeText(url)
    setCopiedId(summary.id)
    setMenuOpenId(null)
    setTimeout(() => setCopiedId(null), 2000)
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="flex items-center gap-2 glass rounded-lg px-3 py-2 focus-within:border-cyan-400/40 transition-colors">
        <Search className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
        <input
          type="text"
          value={query}
          onChange={e => handleSearch(e.target.value)}
          placeholder="Search projects..."
          className="flex-1 bg-transparent font-mono text-xs text-slate-200 placeholder-slate-600 focus:outline-none"
        />
      </div>

      {/* Job list */}
      {loading && summaries.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <span className="font-mono text-xs text-slate-600 animate-pulse">Loading...</span>
        </div>
      ) : summaries.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No projects yet"
          description="Start a new project to generate G-code from your drawings."
          action={{ label: 'New Project', onClick: () => navigate('/new-job') }}
        />
      ) : (
        <div className="space-y-2">
          {summaries.map(s => (
            <div
              key={s.id}
              className="p-4 glass rounded-xl hover:border-cyan-500/25 transition-colors"
            >
              {/* Top row: label + date */}
              <div className="flex items-start justify-between gap-2 mb-1">
                <span className="font-mono text-sm text-slate-200 truncate flex-1">
                  {s.label}
                </span>
                <span className="font-mono text-[11px] text-slate-600 flex-shrink-0">
                  {timeAgo(s.updatedAt)}
                </span>
              </div>

              {/* Meta row */}
              <div className="flex items-center gap-2 mb-4">
                <span className="px-1.5 py-0.5 rounded bg-cyan-500/10 border border-cyan-500/20 font-mono text-[11px] text-cyan-400">
                  {s.material}
                </span>
                <span className="font-mono text-[11px] text-slate-600">
                  {s.messageCount} msg{s.messageCount !== 1 ? 's' : ''}
                </span>
                {copiedId === s.id && (
                  <span className="font-mono text-[11px] text-emerald-400">Link copied</span>
                )}
              </div>

              {/* Actions: prominent Open + kebab menu */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onReopenJob(s.id)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-mono text-xs bg-cyan-500/10 border border-cyan-500/25 text-cyan-400 hover:bg-cyan-500/20 hover:border-cyan-400/50 transition-all"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open
                </button>

                {/* Kebab menu */}
                <div className="relative" ref={menuOpenId === s.id ? menuRef : undefined}>
                  <button
                    onClick={() => {
                      setMenuOpenId(menuOpenId === s.id ? null : s.id)
                      setConfirmDeleteId(null)
                    }}
                    className="flex items-center justify-center w-9 h-9 rounded-lg border border-slate-700 text-slate-500 hover:border-slate-500 hover:text-slate-300 transition-all"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>

                  {menuOpenId === s.id && (
                    <div className="absolute right-0 bottom-full mb-1 w-40 py-1 glass rounded-lg border border-slate-700 shadow-xl z-10">
                      <button
                        onClick={() => handleCopyLink(s)}
                        className="flex items-center gap-2 w-full px-3 py-2 font-mono text-xs text-slate-300 hover:bg-white/5 transition-colors"
                      >
                        <Link2 className="w-3.5 h-3.5" />
                        Copy Link
                      </button>
                      {confirmDeleteId === s.id ? (
                        <button
                          onClick={() => {
                            onDeleteJob(s.id)
                            setSummaries(prev => prev.filter(j => j.id !== s.id))
                            setConfirmDeleteId(null)
                            setMenuOpenId(null)
                          }}
                          className="flex items-center gap-2 w-full px-3 py-2 font-mono text-xs text-red-400 hover:bg-red-500/10 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Confirm Delete
                        </button>
                      ) : (
                        <button
                          onClick={() => setConfirmDeleteId(s.id)}
                          className="flex items-center gap-2 w-full px-3 py-2 font-mono text-xs text-slate-500 hover:text-red-400 hover:bg-red-500/5 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
